import API from "api/client";
import { envConfig } from "config";

const { accountsAPI } = envConfig;

/**
 * @typedef { import('types').AccountUsersResponseItem } AccountUsersResponseItem
 * @typedef { import('types').User } User
 * @typedef { import('types').NetworkFilters } NetworkFilters
 * @typedef { import('types').AccountProperty } AccountProperty
 * @typedef { import('types').AccountPropertySimple } AccountPropertySimple
 * @typedef { import('types').AccountDefaultSettingsUpsert } AccountDefaultSettingsUpsert
 * @typedef { import('types').AccountDefaultSettings } AccountDefaultSettings
 * @typedef { import('types').AssetAccountProperties } AssetAccountProperties
 * @typedef { import('types').AccountCreateRequest } AccountCreateRequest
 * @typedef { import('types').Account } Account
 * @typedef { import('types').AccountExportResponse } AccountExportResponse
 * @typedef { import('types').Product } Product
 */

/**
 * @param {number|string} accountId The ID of the account where the user is being added.
 * @param {Object} userToAdd - The user being added.
 * @param {string} userToAdd.firstName - The firstName of the user to be added.
 * @param {string} userToAdd.lastName - The lastName of the user to be added.
 * @param {string} userToAdd.email - The email for user to be added.
 * @param {string|null} userToAdd.role - The role of the user in the account.
 * @param {bool|null} userToAdd.contract - Does the user have permission to sign the accounts contract.
 * @returns {Promise<{}>}
 */
const addUserToAccount = (accountId, userToAdd) => {
  return API.post(`${accountsAPI}${accountId}/users/add-user`, userToAdd).then((res) => res.data);
};

/**
 * @param {AccountCreateRequest} req
 * @returns {Promise<Account>}
 */
const createAccount = (req) => {
  return API.post(`${accountsAPI}`, req).then((res) => res.data);
};

/**
 * @param {number|string} accountId
 * @param {string} accessKey - access key for account to join a network
 * @returns {Promise<{}>}
 */
const createAccountToNetwork = (accountId, accessKey) => {
  return API.post(`${accountsAPI}${accountId}/networks`, { accessKey: accessKey }).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @param {object} req
 * @param {string} req.billableDesc
 * @param {number} req.costInCents
 * @param {number} req.customResponseCounts
 * @param {Date|string} req.startTime
 * @param {Date|string} req.endTime
 * @returns {Promise<{}>}
 */
const createSubscriptionForAccount = (accountId, req) => {
  return API.post(`${accountsAPI}${accountId}/subscriptions`, req).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @param {string} uuid - network uuid
 * @returns {Promise<{}>}
 */
const deleteAccountToNetwork = (accountId, uuid) => {
  return API.delete(`${accountsAPI}${accountId}/networks/${uuid}`).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @param {string} userId
 * @returns {Promise<void>}
 */
const deleteUserToAccount = (accountId, userId) => {
  return API.delete(`${accountsAPI}${accountId}/users/${userId}`).then((res) => res.data);
};

/**
 * Deprecated: Use the account attached to the current user.
 *
 * @param {number|string} accountId
 * @returns {Promise<import('types').Account>}
 */
const getAccount = (accountId) => {
  return API.get(`${accountsAPI}${accountId}`).then((res) => res.data);
};

/**
 * @typedef {object} SearchRequest
 * @property {string} query
 * @property {string|null|undefined} orderByTerm
 * @property {string|null|undefined} orderTerm
 * @property {number|string} page
 * @property {number|string} size
 */

/**
 *
 * @param {SearchRequest} request
 * @returns {Promise<{results: obj[]}>}
 */
const getAccounts = ({ query, orderByTerm, orderTerm, page = 1, size = 10 }) => {
  return API.get(`${accountsAPI}`, {
    params: { query: query, orderBy: orderByTerm, order: orderTerm, page, size },
  }).then((res) => res.data);
};

/**
 *
 * @returns {Promise<import('types').AccountSimpleWithType[]>}
 */
const getAllAccounts = () => API.get(`${accountsAPI}all`).then((res) => res.data);

/**
 *
 * @param {number|string} accountId
 * @returns {Promise<AccountDefaultSettings>}
 */
const getDefaultSettingsByAccount = (accountId) => {
  return API.get(`${accountsAPI}${accountId}/default-settings`).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @returns {Promise<Network[]>}
 */
const getNetworksByAccount = (accountId) => {
  return API.get(`${accountsAPI}${accountId}/networks`).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @returns {Promise<Category[]>}
 */
const getCategoriesByAccount = (accountId) => {
  return API.get(`${accountsAPI}${accountId}/categories`).then((res) => res.data);
};

/**
 * Returns all network filters for the provided account.
 * @param {number|string} accountId
 * @returns {Promise<NetworkFilters>}
 */
const getNetworkFiltersByAccount = (accountId) => {
  return API.get(`${accountsAPI}${accountId}/network-filters`).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @param {string} subscriptionUuid
 * @returns {Promise<{}>}
 */
const getSubscriptionForAccount = (accountId, subscriptionUuid) => {
  return API.get(`${accountsAPI}${accountId}/subscriptions/${subscriptionUuid}`).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @returns {Promise<{}>}
 */
const getSubscriptionsByAccount = (accountId) => {
  return API.get(`${accountsAPI}${accountId}/subscriptions`).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @returns {Promise<{}>}
 */
const getUserGroupsByAccount = (accountId) => {
  return API.get(`${accountsAPI}${accountId}/usergroups`).then((res) => res.data);
};

/**
 *
 * @param {number} accountId
 * @returns {Promise<AccountUsersResponseItem[]>}
 */
const getUsersByAccount = (accountId) => {
  return API.get(`${accountsAPI}${accountId}/users`).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @param {number|string} userId
 * @returns {Promise<void>}
 */
const reinviteUser = (accountId, userId) => {
  return API.post(`${accountsAPI}${accountId}/users/${userId}/reinvite`).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @param {string} name
 * @returns {Promise<Account>}
 */
const updateAccount = (accountId, name) => {
  return API.put(`${accountsAPI}${accountId}/info`, { name }).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @param {string} subscriptionUuid
 * @param {object} req
 * @param {string} req.billableDesc
 * @param {number} req.costInCents
 * @param {number} req.customResponseCounts
 * @param {Date|string} req.startTime
 * @param {Date|string} req.endTime
 * @returns {Promise<{}>}
 */
const updateSubscriptionForAccount = (accountId, subscriptionUuid, req) => {
  return API.put(`${accountsAPI}${accountId}/subscriptions/${subscriptionUuid}`, req).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @param {string} userId
 * @param {string} roleName
 * @returns {Promise<void>}
 */
const updateUserToAccount = (accountId, userId, roleName) => {
  return API.put(`${accountsAPI}${accountId}/users/${userId}`, { roleName: roleName }).then((res) => res.data);
};

/**
 * @param {number|string} accountId - The account's ID being upserted.
 * @param {AccountDefaultSettingsUpsert} settings - The settings being upserted.
 * @returns {Promise<AccountDefaultSettings>}
 */
const upsertAccountDefaultSettings = (accountId, settings) => {
  return API.put(`${accountsAPI}${accountId}/default-settings`, settings).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @returns {Promise<AccountProperty[]>}
 */
const getAccountProperties = (accountId) => {
  return API.get(`${accountsAPI}${accountId}/properties`).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @param {AccountPropertySimple[]} request
 * @returns {Promise<AccountPropertySimple[]>}
 */
const upsertAccountProperties = (accountId, request) => {
  return API.put(`${accountsAPI}${accountId}/properties`, { properties: request }).then((res) => res.data);
};

/**
 * Exports users of an account as a CSV file.
 * Returns a response containing a presigned URL for downloading the file.
 * You can pass this presigned URL to the `downloadFileWithoutPopup` utility to download the file.
 *
 * @param {number|string} accountId
 * @returns {Promise<AccountExportResponse>}
 */
const exportUsersAsCSV = (accountId) => {
  return API.post(`${accountsAPI}${accountId}/export-users`, null).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @returns {Promise<AssetAccountProperties>}
 */
const getAssetAccountProperties = (accountId) => {
  return API.get(`${accountsAPI}${accountId}/asset-properties`).then((res) => res.data);
};

/**
 *
 * @param {number|string} accountId
 * @returns {Promise<Product[]>}
 */
const getProductsByAccount = (accountId) => {
  return API.get(`${accountsAPI}${accountId}/products`).then((res) => res.data);
};

export default {
  addUTA: addUserToAccount,
  create: createAccount,
  createATN: createAccountToNetwork,
  createSubscription: createSubscriptionForAccount,
  deleteATN: deleteAccountToNetwork,
  deleteUTA: deleteUserToAccount,
  exportUsers: exportUsersAsCSV,
  get: getAccount,
  getAll: getAllAccounts,
  getDefaultSettings: getDefaultSettingsByAccount,
  getProperties: getAccountProperties,
  getSubscription: getSubscriptionForAccount,
  list: getAccounts,
  listCategories: getCategoriesByAccount,
  listNetworks: getNetworksByAccount,
  listNetworkFilters: getNetworkFiltersByAccount,
  listSubscriptions: getSubscriptionsByAccount,
  listUserGroups: getUserGroupsByAccount,
  listUsers: getUsersByAccount,
  reinviteUser: reinviteUser,
  update: updateAccount,
  upsertProperties: upsertAccountProperties,
  updateSubscription: updateSubscriptionForAccount,
  updateUTA: updateUserToAccount,
  upsertDefaultSettings: upsertAccountDefaultSettings,
  getAssetProperties: getAssetAccountProperties,
  listProducts: getProductsByAccount,
};
